import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';

interface Meter {
  id: number;
  meter_name: string;
  meter_code: string;
  meter_type: string;
  unit: string;
}

interface StreamConfig {
  meter_id: number;
  stream_name: string;
  stream_endpoint: string;
  camera_ip: string;
  poll_seconds: number;
  enabled: boolean;
  notes: string;
}

const STORAGE_KEY = 'flowvision_stream_config_v1';

const FlowVision: React.FC = () => {
  const [meters, setMeters] = useState<Meter[]>([]);
  const [configs, setConfigs] = useState<StreamConfig[]>([]);
  const [search, setSearch] = useState('');
  const [meterType, setMeterType] = useState('All');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMeters();
  }, []);

  const fetchMeters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/energy-management/meters', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const meterList: Meter[] = response.data || [];
      setMeters(meterList);

      const raw = localStorage.getItem(STORAGE_KEY);
      const saved: StreamConfig[] = raw ? JSON.parse(raw) : [];
      const savedMap = new Map(saved.map((s) => [s.meter_id, s]));

      const merged = meterList.map((meter) => {
        const existing = savedMap.get(meter.id);
        return (
          existing || {
            meter_id: meter.id,
            stream_name: `${meter.meter_code} Stream`,
            stream_endpoint: `http://stream.local/meter/${meter.meter_code.toLowerCase()}`,
            camera_ip: '',
            poll_seconds: 15,
            enabled: true,
            notes: '',
          }
        );
      });

      setConfigs(merged);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load meters for FlowVision');
    }
  };

  const filteredMeters = useMemo(() => {
    const text = search.toLowerCase().trim();
    return meters.filter((meter) => {
      const typeMatch = meterType === 'All' || meter.meter_type === meterType;
      const textMatch =
        !text ||
        meter.meter_name.toLowerCase().includes(text) ||
        meter.meter_code.toLowerCase().includes(text);
      return typeMatch && textMatch;
    });
  }, [meters, search, meterType]);

  const filteredConfigs = filteredMeters.map((meter) => {
    const cfg = configs.find((c) => c.meter_id === meter.id);
    return { meter, cfg };
  });

  const updateConfig = (meterId: number, field: keyof StreamConfig, value: string | number | boolean) => {
    setConfigs((prev) =>
      prev.map((cfg) =>
        cfg.meter_id === meterId
          ? {
              ...cfg,
              [field]: value,
            }
          : cfg
      )
    );
  };

  const saveLocalConfig = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
    setStatus('FlowVision configuration saved locally (dummy mode).');
  };

  const testStream = (meterId: number) => {
    const cfg = configs.find((c) => c.meter_id === meterId);
    if (!cfg) return;

    if (!cfg.stream_endpoint || !cfg.camera_ip) {
      setError('Please provide stream endpoint and camera IP before testing.');
      return;
    }

    setError(null);
    setStatus(`Dummy test successful for meter ${meterId}. Stream and camera mapping are configured.`);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        FlowVision
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure a data stream and camera endpoint for each meter. This is a dummy interface to prepare for the upcoming live stream integration API.
      </Typography>

      {status && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatus(null)}>
          {status}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            label="Search meter"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="By meter code or name"
            sx={{ minWidth: { md: 260 }, width: { xs: '100%', md: 'auto' } }}
          />

          <FormControl size="small" sx={{ minWidth: { md: 180 }, width: { xs: '100%', md: 'auto' } }}>
            <InputLabel>Meter Type</InputLabel>
            <Select
              value={meterType}
              label="Meter Type"
              onChange={(e) => setMeterType(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Steam">Steam</MenuItem>
              <MenuItem value="Power">Power</MenuItem>
              <MenuItem value="Water">Water</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <Chip label={`Meters: ${filteredMeters.length}`} color="primary" variant="outlined" />
          <Button variant="contained" onClick={saveLocalConfig}>
            Save Dummy Config
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Meter</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Stream Name</TableCell>
              <TableCell>Data Stream Endpoint</TableCell>
              <TableCell>Camera IP / URL</TableCell>
              <TableCell align="right">Poll (sec)</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredConfigs.map(({ meter, cfg }) => {
              if (!cfg) return null;
              return (
                <TableRow key={meter.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {meter.meter_code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {meter.meter_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{meter.meter_type}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={cfg.stream_name}
                      onChange={(e) => updateConfig(meter.id, 'stream_name', e.target.value)}
                      sx={{ minWidth: 160 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={cfg.stream_endpoint}
                      onChange={(e) => updateConfig(meter.id, 'stream_endpoint', e.target.value)}
                      placeholder="http://stream.local/meter/..."
                      sx={{ minWidth: 250 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={cfg.camera_ip}
                      onChange={(e) => updateConfig(meter.id, 'camera_ip', e.target.value)}
                      placeholder="rtsp://192.168.x.x/live"
                      sx={{ minWidth: 220 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={cfg.poll_seconds}
                      onChange={(e) => updateConfig(meter.id, 'poll_seconds', Math.max(1, parseInt(e.target.value || '1', 10)))}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Select
                      size="small"
                      value={cfg.enabled ? 'yes' : 'no'}
                      onChange={(e) => updateConfig(meter.id, 'enabled', e.target.value === 'yes')}
                      sx={{ width: 90 }}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={cfg.notes}
                      onChange={(e) => updateConfig(meter.id, 'notes', e.target.value)}
                      placeholder="Optional comments"
                      sx={{ minWidth: 180 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="outlined" onClick={() => testStream(meter.id)}>
                      Test
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FlowVision;
